import React, { useState } from 'react';
import s from './s.module.less';
import PPAOnboradingHeader from 'components/PPAOnboardingHeader';
import { useNavigate } from 'react-router-dom';
import { TTeamMemberPractice, EPortalStatus } from 'types/group';
import { ERoleCode } from 'types/enumItems';
import { Tooltip } from 'antd';

//https://www.figma.com/design/aGY2cI5da2oGElG6GEd0Wo/Group-Practice?node-id=4067-15830&m=dev

const mockData: TTeamMemberPractice[] = [
    {
        id: 1,
        firstName: 'David',
        lastName: 'Park',
        email: 'david.park@example.com',
        tel: '1234567890',
        role: ERoleCode.GROUP_PRACTICE_MANAGER,
        permissions: [],
        portalStatus: EPortalStatus.SIGNED_UP,
        specialties: ['Infections', 'Preventive & Primary Care', "Women's Health"],
        states: ['CA', 'NY'],
    },
    {
        id: 2,
        firstName: 'Emily',
        lastName: 'Carter',
        email: 'emily.carter@example.com',
        tel: '1234567891',
        role: ERoleCode.GROUP_CLINICAL_ASSISTANT,
        permissions: [],
        portalStatus: EPortalStatus.SIGNED_UP,
        specialties: ['Psychiatric & mental health', 'Weight loss'],
        states: ['AL', 'AK', 'CA', 'NY'],
    },
    {
        id: 3,
        firstName: 'Rachel',
        lastName: 'Stein',
        email: 'rachel.stein@example.com',
        tel: '1234567892',
        role: ERoleCode.GROUP_CLINICAL_ASSISTANT,
        permissions: [],
        portalStatus: EPortalStatus.SIGNED_UP,
        specialties: ["Men's Health", 'Weight loss', 'Preventive & Primary Care'],
        states: ['CA'],
    },
    {
        id: 4,
        firstName: 'James',
        lastName: 'Patel',
        email: 'james.patel@example.com',
        tel: '1234567893',
        role: ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
        permissions: [],
        portalStatus: EPortalStatus.SIGNED_UP,
        specialties: [
            'Psychiatric & mental health',
            'Dermatology',
            "Men's Health",
            "Women's Health",
            'Preventive & Primary Care',
        ],
        states: ['CA', 'NY', 'FL', 'TX'],
    },
];

const MAX_SPECIALTIES_LENGTH = 60;

function getRoleBadgeLabel(role: ERoleCode): string {
    if (role === ERoleCode.GROUP_CLINICAL_ASSISTANT) return 'Group clinical assistant (MA)';
    return role;
}

function formatSpecialties(specialties: string[]): { display: string; isTruncated: boolean; full: string } {
    const full = specialties.join(', ');
    if (full.length <= MAX_SPECIALTIES_LENGTH) {
        return { display: full, isTruncated: false, full };
    }
    const truncated = `${full.substring(0, MAX_SPECIALTIES_LENGTH).replace(/,\s*$/, '')}...`;
    return { display: truncated, isTruncated: true, full };
}

const OfficeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M3.75 21H20.25M4.5 3H19.5M5.25 3V21M18.75 3V21M9 6.75H10.5M9 10.5H10.5M9 14.25H10.5M13.5 6.75H15M13.5 10.5H15M13.5 14.25H15M9 21V17.625C9 17.004 9.504 16.5 10.125 16.5H13.875C14.496 16.5 15 17.004 15 17.625V21"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const LocationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M15 10.5C15 11.2956 14.6839 12.0587 14.1213 12.6213C13.5587 13.1839 12.7956 13.5 12 13.5C11.2044 13.5 10.4413 13.1839 9.87868 12.6213C9.31607 12.0587 9 11.2956 9 10.5C9 9.70435 9.31607 8.94129 9.87868 8.37868C10.4413 7.81607 11.2044 7.5 12 7.5C12.7956 7.5 13.5587 7.81607 14.1213 8.37868C14.6839 8.94129 15 9.70435 15 10.5Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M19.5 10.5C19.5 17.642 12 21.75 12 21.75C12 21.75 4.5 17.642 4.5 10.5C4.5 8.51088 5.29018 6.60322 6.6967 5.1967C8.10322 3.79018 10.0109 3 12 3C13.9891 3 15.8968 3.79018 17.3033 5.1967C18.7098 6.60322 19.5 8.51088 19.5 10.5Z"
            stroke="#6B7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface PracticeCardProps {
    data: TTeamMemberPractice;
    onClick?: (data: TTeamMemberPractice) => void;
}

function PracticeCard({ data, onClick }: PracticeCardProps) {
    const { display, isTruncated, full } = formatSpecialties(data.specialties);
    const statesText = data.states.join(', ');

    return (
        <div className={s.card} onClick={() => onClick?.(data)}>
            <div className={s.badge}>
                {getRoleBadgeLabel(data.role)}
            </div>
            <div className={s.name}>
                {data.firstName} {data.lastName}
            </div>
            <div className={s.infoRow}>
                <OfficeIcon />
                {isTruncated ? (
                    <Tooltip title={full} overlayClassName={s.tooltip}>
                        <span className={s.infoText}>{display}</span>
                    </Tooltip>
                ) : (
                    <span className={s.infoText}>{display}</span>
                )}
            </div>
            <div className={s.infoRow}>
                <LocationIcon />
                <span className={s.infoText}>{statesText}</span>
            </div>
        </div>
    );
}

export default function ProviderSelectOrgPage() {
    const [memberPracticeList] = useState<TTeamMemberPractice[]>(mockData);
    const navigate = useNavigate();

    const handleCardClick = () => {
        // TODO: navigate to the selected practice dashboard
        navigate('/dashboard');
    };

    return (
        <div className={s.wrap}>
            <PPAOnboradingHeader showLogout />

            <div className={s.content}>
                <div className={s.contentIn}>
                    <div className={s.titleSection}>
                        <h1 className={s.heading}>Select a practice</h1>
                        <p className={s.subtitle}>Choose which practice you&apos;d like to access</p>
                    </div>
                    <div className={s.cardGrid}>
                        {memberPracticeList.map((item) => (
                            <PracticeCard key={item.id} data={item} onClick={handleCardClick} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
